import React, { ReactElement } from 'react';
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
  GridCellProps,
  GridToolbar,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect, useState } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';

import { GetString } from '../StringResourceManager';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { LoadingPanel } from '../LoadingPanel';
import { ReportExportStatus } from '../../Interfaces/Reports/ReportExportStatus';
import {
  fetchReportExportStatusList,
  fetchReportDownloadURL,
  resetReportDownloadURL,
} from '../../store/Reports/ReportsActions';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import {
  commandStatusIconMap,
  getCommandStatusToEnum,
  getDate,
  getLocalDateTime,
  useChipStyle,
} from '../../utils/utils';
import CountDetailsDialog from './CountDetailsDialog';
import { CommandHistoryDropDownFilterCell } from '../CommandsHistory/CommandHistoryDropDownFilterCell';
import { Chip } from '@progress/kendo-react-buttons';
import { commandStatusTextMap } from '../../utils/textMappings';
import { Button } from '@progress/kendo-react-buttons';
import { ReportDownloadURLResponseResult } from '../../Interfaces/Reports/ReportDownloadURLResponseResult';
import { ReportExportListRequest } from '../../Interfaces/Reports/ReportExportListRequest';
import { ReportExportSource } from '../../Interfaces/Reports/ReportExportRequestBody';

const ReportExportStatusesGrid: React.FC = (): ReactElement => {
  const dispatch: Dispatch<any> = useDispatch();

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const defaultDataState: State = {
    skip: 0,
    take: 100,
    sort: [],
    filter: undefined,
    group: [],
  };

  const [dataState, setDataState] = React.useState<State>(defaultDataState);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const reportExportStatusList: ReportExportStatus[] = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportExportStatusList,
  );

  const reportDownloadURL: ReportDownloadURLResponseResult = useSelector(
    (state: ApplicationState) => state.reportsReducer.reportDownloadURL,
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) =>
      state.reportsReducer.isFetchingReportExportStatusList,
  );

  const INVALID_DOWNLOAD_URL = GetString('INVALID_DOWNLOAD_URL');

  useEffect(() => {
    getReportExportStatusList();
  }, []);

  const getReportExportStatusList = () => {
    const requestedFrom = new Date();
    requestedFrom.setDate(requestedFrom.getDate() - 30);
    requestedFrom.setHours(0, 0, 0, 0);

    const requestedTo = new Date();
    requestedTo.setDate(requestedTo.getDate() + 1);
    requestedTo.setHours(0, 0, 0, 0);

    let request: ReportExportListRequest = {
      Status: '',
      RequestedFrom: requestedFrom,
      RequestedTo: requestedTo,
      Source: ReportExportSource.VisionIQ,
    };

    fetchReportExportStatusList(dispatch, request);
  };

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const StartDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="StartDateTime"
      />
    );
  };

  const StartDateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.StartDateTime != undefined &&
      dataItem.StartDateTime != '0001-01-01T00:00:00'
    ) {
      return <td>{getLocalDateTime(dataItem.StartDateTime)}</td>;
    }
    return <td></td>;
  };

  const EndDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="EndDateTime"
      />
    );
  };

  const EndDateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.EndDateTime != undefined &&
      dataItem.EndDateTime != '0001-01-01T00:00:00'
    ) {
      return <td>{getLocalDateTime(dataItem.EndDateTime)}</td>;
    }
    return <td></td>;
  };

  const CompletedDateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CompletedOn"
      />
    );
  };

  const CompletedDateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (
      dataItem?.CompletedOn != undefined &&
      dataItem.CompletedOn != '0001-01-01T00:00:00'
    ) {
      return <td>{getLocalDateTime(dataItem.CompletedOn)}</td>;
    }
    return <td></td>;
  };

  const CountCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    const result =
      props.field == 'SiteNumbers'
        ? dataItem.SiteNumbers.filter((x: string) => x?.trim())
        : dataItem.LaneNumbers.filter((x: string) => x?.trim());

    if (result.length != 0) {
      return (
        <td>
          <CountDetailsDialog data={dataItem} field={props.field} />
        </td>
      );
    }
    return <td></td>;
  };

  const FileSizeCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem.ReportSizeInKB) {
      return (
        <td>
          {Number(dataItem.ReportSizeInKB).toLocaleString('en-US') + ' KB'}
        </td>
      );
    }
    return <td></td>;
  };

  const StatusCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;

    const status: any = getCommandStatusToEnum(dataItem.Status.toString());

    const commandStatus: any = commandStatusTextMap(status);

    let chipStyle: any = useChipStyle(status);

    if (status) {
      return (
        <td>
          <Chip
            className={chipStyle().root}
            text={GetString(commandStatus)}
            svgIcon={commandStatusIconMap(status)}
            onClick={() => alert(dataItem.ExecutionMessage)}
          />
        </td>
      );
    }
    return null;
  };

  const handleDownloadButtonClick = (filePath: any) => {
    fetchReportDownloadURL(dispatch, filePath);
  };

  const DownloadCellWrapper = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    const isDisabled =
      dataItem.FilePath.includes('sftp') || dataItem.Status !== 'Completed';
    if (dataItem) {
      return (
        <td>
          <Button
            themeColor={isDisabled ? 'error' : 'primary'}
            type="button"
            onClick={() => handleDownloadButtonClick(dataItem.FilePath)}
            disabled={isDisabled}
          >
            {GetString('DOWNLOAD')}
          </Button>
        </td>
      );
    }
    return <td></td>;
  };

  const processDownload = async () => {
    try {
      new URL(reportDownloadURL.DownloadURL);
      const link = document.createElement('a');
      link.href = reportDownloadURL.DownloadURL;
      link.style.display = 'none';
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
      alert(INVALID_DOWNLOAD_URL);
    }
  };

  useEffect(() => {
    if (
      reportDownloadURL.IsValid &&
      reportDownloadURL.DownloadURL != undefined
    ) {
      processDownload();
      setTimeout(() => {
        resetReportDownloadURL(dispatch);
      }, 1000);
    } else if (
      !reportDownloadURL.IsValid &&
      reportDownloadURL.Message != undefined
    ) {
      alert(INVALID_DOWNLOAD_URL);
      setTimeout(() => {
        resetReportDownloadURL(dispatch);
      }, 1000);
    }
  }, [reportDownloadURL]);

  return (
    <LocalizationProvider language={currentLocale.language}>
      {loading && LoadingPanel}
      <Grid
        style={{ height: '650px', width: '1300px' }}
        sortable={true}
        filterable={true}
        groupable={true}
        resizable={true}
        pageable={{
          buttonCount: 4,
          pageSizes: [10, 20, 50, 100, 500],
        }}
        data={process(reportExportStatusList, dataState)}
        {...dataState}
        onDataStateChange={(e: GridDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
      >
        <GridToolbar>
          <Button icon="refresh" onClick={() => getReportExportStatusList()}>
            {GetString('REFRESH')}
          </Button>
        </GridToolbar>

        <GridColumn
          field="ReportID"
          title={GetString('REPORT_ID')}
          width="150px"
        />
        <GridColumn
          field="SiteNumbers"
          title={GetString('STORES_COUNT')}
          width="140px"
          cells={{ data: CountCellWrapper }}
        />
        <GridColumn
          field="LaneNumbers"
          title={GetString('LANES_COUNT')}
          width="140px"
          cells={{ data: CountCellWrapper }}
        />
        <GridColumn
          field="StartDateTime"
          title={GetString('START_DATE_TIME_COLUMN')}
          width="170px"
          cells={{ data: StartDateTimeCell, filterCell: StartDateFilterCell }}
        />
        <GridColumn
          field="EndDateTime"
          title={GetString('END_DATE_TIME_COLUMN')}
          width="170px"
          cells={{ data: EndDateTimeCell, filterCell: EndDateFilterCell }}
        />
        <GridColumn
          field="CompletedOn"
          title={GetString('COMPLETED_STR')}
          width="150px"
          cells={{
            data: CompletedDateTimeCell,
            filterCell: CompletedDateFilterCell,
          }}
        />
        <GridColumn
          field="Status"
          title={GetString('STATUS_TITLE')}
          width="150px"
          cells={{
            data: StatusCell,
            filterCell: CommandHistoryDropDownFilterCell,
          }}
        />
        <GridColumn
          field="FilePath"
          title={GetString('FILE')}
          width="150px"
          cells={{ data: DownloadCellWrapper }}
        />
        <GridColumn
          field="ReportSizeInKB"
          title={GetString('FILE_SIZE')}
          width="150px"
          cells={{ data: FileSizeCellWrapper }}
        />
        <GridColumn
          field="Language"
          title={GetString('LANGUAGE_LABEL')}
          width="150px"
        />
      </Grid>
    </LocalizationProvider>
  );
};

export default ReportExportStatusesGrid;
